import { z } from 'zod';

import {
  COMPANY_NAME_MAX_LENGTH,
  NAME_MAX_LENGTH,
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from '../constants/limits.constant';

const EMAIL_MAX_LENGTH = 100;
const PHONE_MAX_LENGTH = 20;
const BUSINESS_NUMBER_FIELD_MAX = 20;
const REPRESENTATIVE_NAME_MAX = 50;

/**
 * 프론트 입력값 검증 스키마.
 * 백엔드 DTO(RegisterDto / LoginDto / ResetPasswordDto)와 규칙을 동기화한다.
 *
 * ── 백엔드와 맞춘 제약 ──────────────────────────────────────────
 *   email       : 유효한 이메일 형식, max 100
 *   password    : 8자 이상, max 200 (RegisterDto 기준)
 *   name        : 1~50자
 *   phone       : 선택, max 20
 *   businessNumber/companyName : BUSINESS일 때 필수
 *
 * ── 프론트에서 추가한 규칙 ─────────────────────────────────────
 *   password 복잡도(영문+숫자) — UX 가드용. 서버 강제 원하면 백엔드 DTO 에도 반영.
 *   passwordConfirm 일치 검증
 */

// ── 공통 필드 ────────────────────────────────────────────────────

export const emailField = z
  .string()
  .min(1, '이메일을 입력해주세요.')
  .max(EMAIL_MAX_LENGTH, '이메일이 너무 깁니다.')
  .email('이메일 형식이 올바르지 않습니다.');

export const passwordField = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `비밀번호는 ${PASSWORD_MIN_LENGTH}자 이상이어야 합니다.`)
  .max(PASSWORD_MAX_LENGTH, '비밀번호가 너무 깁니다.')
  .refine(
    (v) => /[A-Za-z]/.test(v) && /\d/.test(v),
    '비밀번호는 영문과 숫자를 모두 포함해야 합니다.',
  );

// ── 로그인 식별자 ────────────────────────────────────────────────
// **이메일로는 로그인할 수 없다.** 개인은 아이디 한 칸, 기업은 회사코드 + 아이디
// 두 칸으로 받고 서버가 `odin@howcost` 로 조립한다.

/** 아이디의 로컬 파트 — 백엔드 LOGIN_ID_LOCAL_REGEX 와 동일 */
export const loginIdField = z
  .string()
  .min(1, '아이디를 입력해주세요.')
  .regex(/^[a-z0-9_]{4,20}$/, '영문 소문자/숫자/_ 4~20자로 입력해주세요.');

/** 회사코드 — 백엔드 ORG_SLUG_REGEX 와 동일. 언더스코어·하이픈 불가. */
export const orgSlugField = z
  .string()
  .min(1, '회사코드를 입력해주세요.')
  .regex(/^[a-z0-9]{3,20}$/, '영문 소문자/숫자 3~20자로 입력해주세요.');

/**
 * 개인 로그인 아이디 칸 — 형식은 보지 않는다. 구버전 아이디(대문자·점·한글·이메일형)를 그대로 쳐도
 * 화면이 신 아이디 규칙으로 바꿔 보낸다(legacyToLoginId). 틀린 아이디는 서버가 "불일치"로 답한다.
 */
const personalLoginIdField = z
  .string()
  .trim()
  .min(1, '아이디를 입력해주세요.')
  .max(50, '아이디가 너무 깁니다.');

export const personalLoginSchema = z.object({
  loginId: personalLoginIdField,
  password: z.string().min(1, '비밀번호를 입력해주세요.'),
});

export const businessLoginSchema = z.object({
  orgSlug: orgSlugField,
  loginId: loginIdField,
  password: z.string().min(1, '비밀번호를 입력해주세요.'),
});

export interface LoginFormValues {
  orgSlug: string;
  loginId: string;
  password: string;
}

// ── 회원가입 공통 필드 ──────────────────────────────────────────

/** 닉네임(별명) */
export const nicknameField = z
  .string()
  .min(NICKNAME_MIN_LENGTH, `닉네임은 ${NICKNAME_MIN_LENGTH}자 이상이어야 합니다.`)
  .max(NICKNAME_MAX_LENGTH, `닉네임은 ${NICKNAME_MAX_LENGTH}자 이하여야 합니다.`);

// ── 회원가입 ─────────────────────────────────────────────────────

const registerBase = z.object({
  email: emailField,
  loginId: loginIdField,
  password: passwordField,
  passwordConfirm: z.string().min(1, '비밀번호 확인을 입력해주세요.'),
  name: z
    .string()
    .min(1, '이름을 입력해주세요.')
    .max(NAME_MAX_LENGTH, `이름은 ${NAME_MAX_LENGTH}자 이하여야 합니다.`),
  nickname: nicknameField,
  phone: z
    .string()
    .max(PHONE_MAX_LENGTH, `연락처는 ${PHONE_MAX_LENGTH}자 이하여야 합니다.`)
    .optional()
    .or(z.literal('')),
  userType: z.enum(['INDIVIDUAL', 'BUSINESS']),
  businessNumber: z
    .string()
    .max(BUSINESS_NUMBER_FIELD_MAX, `사업자등록번호는 ${BUSINESS_NUMBER_FIELD_MAX}자 이하여야 합니다.`)
    .optional()
    .or(z.literal('')),
  companyName: z
    .string()
    .max(COMPANY_NAME_MAX_LENGTH * 2, `회사명은 ${COMPANY_NAME_MAX_LENGTH * 2}자 이하여야 합니다.`)
    .optional()
    .or(z.literal('')),
  agreeTerms: z.boolean(),
  agreePrivacy: z.boolean(),
  agreeMarketing: z.boolean().optional(),
  agreeEvent: z.boolean().optional(),
});

function businessRefine(v: {
  userType: string;
  businessNumber?: string;
  companyName?: string;
}): boolean {
  return v.userType !== 'BUSINESS' || (!!v.businessNumber && !!v.companyName);
}

export const registerSchema = registerBase
  .refine((v) => v.password === v.passwordConfirm, {
    path: ['passwordConfirm'],
    message: '비밀번호가 일치하지 않습니다.',
  })
  .refine(businessRefine, {
    path: ['companyName'],
    message: '기업 가입 시 사업자등록번호와 회사명은 필수입니다.',
  })
  .refine((v) => v.agreeTerms, {
    path: ['agreeTerms'],
    message: '이용약관에 동의해주세요.',
  })
  .refine((v) => v.agreePrivacy, {
    path: ['agreePrivacy'],
    message: '개인정보 수집·이용에 동의해주세요.',
  });

export type RegisterFormValues = z.infer<typeof registerBase>;

/* ── 위저드 단계별 스키마 (각 스텝 진행 시 부분 검증) ───────────── */

/** STEP 약관: 필수 2종 동의 */
export const signupAgreementsSchema = z.object({
  agreeTerms: z.literal(true, { message: '이용약관에 동의해주세요.' }),
  agreePrivacy: z.literal(true, { message: '개인정보 수집·이용에 동의해주세요.' }),
  agreeMarketing: z.boolean().optional(),
  agreeEvent: z.boolean().optional(),
});

/**
 * STEP 계정: 이메일/아이디/비밀번호 (+기업이면 회사코드).
 *
 * 회사코드는 이 단계에서 받는다 — 아이디와 함께 최종 로그인 식별자를 이루므로
 * 중복 확인을 같이 돌려야 "아이디는 되는데 회사코드가 안 된다"를 한 번에 안내한다.
 */
export const signupAccountSchema = z
  .object({
    email: emailField,
    loginId: loginIdField,
    orgSlug: z.string().optional().or(z.literal('')),
    userType: z.enum(['INDIVIDUAL', 'BUSINESS']),
    password: passwordField,
    passwordConfirm: z.string().min(1, '비밀번호 확인을 입력해주세요.'),
  })
  .refine((v) => v.password === v.passwordConfirm, {
    path: ['passwordConfirm'],
    message: '비밀번호가 일치하지 않습니다.',
  })
  .refine(
    (v) =>
      v.userType !== 'BUSINESS' ||
      orgSlugField.safeParse(v.orgSlug ?? '').success,
    {
      path: ['orgSlug'],
      message: '회사코드는 영문 소문자/숫자 3~20자로 입력해주세요.',
    },
  );

/** 국내 휴대폰 번호 — 하이픈 없는 숫자. 백엔드 KR_MOBILE_REGEX 와 동기화. */
export const phoneField = z
  .string()
  .min(1, '휴대폰 번호를 입력해주세요.')
  .max(PHONE_MAX_LENGTH, '휴대폰 번호가 너무 깁니다.')
  .regex(/^01[016789]\d{7,8}$/, '휴대폰 번호 형식이 올바르지 않습니다.');

/** STEP 프로필: 이름/닉네임/연락처(+기업정보) */
export const signupProfileSchema = z
  .object({
    name: z
      .string()
      .min(1, '이름을 입력해주세요.')
      .max(NAME_MAX_LENGTH, `이름은 ${NAME_MAX_LENGTH}자 이하여야 합니다.`),
    nickname: nicknameField,
    phone: phoneField,
    userType: z.enum(['INDIVIDUAL', 'BUSINESS']),
    businessNumber: z
      .string()
      .max(BUSINESS_NUMBER_FIELD_MAX)
      .optional()
      .or(z.literal('')),
    companyName: z
      .string()
      .max(COMPANY_NAME_MAX_LENGTH * 2)
      .optional()
      .or(z.literal('')),
    representativeName: z
      .string()
      .max(REPRESENTATIVE_NAME_MAX)
      .optional()
      .or(z.literal('')),
    openDate: z.string().optional().or(z.literal('')),
  })
  .refine(
    (v) =>
      v.userType !== 'BUSINESS' ||
      (!!v.businessNumber && !!v.companyName && !!v.representativeName && !!v.openDate),
    {
      path: ['companyName'],
      message: '기업 가입 시 사업자번호·회사명·대표자명·개업일자는 필수입니다.',
    },
  );

// ── 비밀번호 재설정 ─────────────────────────────────────────────

/**
 * 재설정 요청 — 이메일만으로는 계정이 특정되지 않는다(이메일이 org 단위 unique).
 * 로그인과 같은 축으로 아이디(+기업이면 회사코드)를 함께 받는다.
 */
export const personalPasswordResetSchema = z.object({
  email: emailField,
  loginId: loginIdField,
});

export const businessPasswordResetSchema = z.object({
  email: emailField,
  orgSlug: orgSlugField,
  loginId: loginIdField,
});

export interface RequestPasswordResetFormValues {
  email: string;
  orgSlug: string;
  loginId: string;
}

/** 아이디 찾기 — 휴대폰 인증(OTP) 선행. */
export const findLoginIdByPhoneSchema = z.object({ phone: phoneField });

/** 아이디 찾기 — 이메일 발송. 결과는 응답이 아니라 메일로 온다. */
export const findLoginIdByEmailSchema = z.object({ email: emailField });

/** 가입정보 확인 후 이메일 재설정. */
export const signupRecoveryEmailSchema = z.object({ newEmail: emailField });

export const resetPasswordSchema = z
  .object({
    newPassword: passwordField,
    passwordConfirm: z.string().min(1, '비밀번호 확인을 입력해주세요.'),
  })
  .refine((v) => v.newPassword === v.passwordConfirm, {
    path: ['passwordConfirm'],
    message: '비밀번호가 일치하지 않습니다.',
  });
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

// ── 마이페이지: 휴대폰/이메일 변경 (민감작업 — 현재 비밀번호 재확인) ───────

const currentPasswordField = z
  .string()
  .min(1, '현재 비밀번호를 입력해주세요.');

/** 휴대폰 변경 — OTP 인증 완료(verified)는 폼 외부 상태로 별도 가드. */
export const phoneChangeSchema = z.object({
  phone: phoneField,
  currentPassword: currentPasswordField,
});
export type PhoneChangeFormValues = z.infer<typeof phoneChangeSchema>;

/** 이메일 변경 요청 */
export const emailChangeSchema = z.object({
  newEmail: emailField,
  currentPassword: currentPasswordField,
});
export type EmailChangeFormValues = z.infer<typeof emailChangeSchema>;
